<template>
  <v-dialog v-model="internalModal" max-width="600">
    <v-card class="modal-card">
      <v-card-title class="modal-title">Change Wound Laterality</v-card-title>
      <v-card-text>
        <!-- CAPTCHA error alert -->
        <Alert
          v-if="captchaError"
          color="warning"
          class="mb-4"
        >
          Enter the correct CAPTCHA to save the changes.
        </Alert>

        <div class="modal-section">
          <div class="modal-label">Current location:</div>
          <div class="modal-current">{{ currentLocationText || '—' }}</div>
        </div>

        <div class="modal-section">
          <div class="modal-label">Change to:</div>
          <v-radio-group v-model="selectedLaterality" row class="modal-radio-group" hide-details>
            <v-radio label="Left" value="Left"></v-radio>
            <v-radio label="Right" value="Right"></v-radio>
          </v-radio-group>
        </div>

        <div class="modal-preview-box">
          <span class="preview-arrow">→</span>
          <span class="preview-label">Preview:</span>
          <div class="preview-text">{{ previewText }}</div>
        </div>

        <div class="modal-captcha-section">
          <div class="captcha-image-row">
            <captcha ref="captchaRef" @update="onCaptchaGenerated" />
          </div>
          <v-text-field
            v-model="captchaInput"
            placeholder="Enter CAPTCHA"
            class="captcha-input"
            :maxlength="generatedCaptcha.length || 6"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="characters"
            spellcheck="false"
            dense
            hide-details
            :error="captchaError"
            @input="onCaptchaInput"
          />
        </div>
      </v-card-text>

      <v-card-actions class="modal-actions">
        <btn
          label="Cancel"
          color="secondary"
          :icon="icons.mdiCancel"
          @click="close"
        ></btn>
        <v-spacer></v-spacer>
        <btn
          label="Save"
          :icon="icons.mdiCheck"
          :disabled="!captchaValid"
          @click="save"
        ></btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Alert from '@/components/elements/Alert.vue';
import Captcha from '@/components/elements/Captcha.vue';
import { mdiCancel, mdiCheck } from '@mdi/js';

export default {
  name: 'ChangeLateralityModal',
  components: { Captcha, Alert },
  props: {
    modalState: { type: Boolean, default: false },
    currentLocationText: { type: String, default: '' },
  },
  data() {
    return {
      internalModal: this.modalState,
      selectedLaterality: 'Left',
      generatedCaptcha: '',
      captchaInput: '',
      captchaError: false,
      icons: {
        mdiCancel,
        mdiCheck,
      },
    }
  },
  watch: {
    modalState(val) {
      this.internalModal = val
    },
    internalModal(val) {
      this.$emit('update:modalState', val)
      if (!val) {
        // reset inputs when closed
        this.resetCaptcha()
      }
    },
  },
  computed: {
    captchaValid() {
      return (
        this.generatedCaptcha &&
        this.captchaInput &&
        this.captchaInput.toUpperCase().trim() === this.generatedCaptcha.toUpperCase().trim()
      )
    },
    previewText() {
      const text = this.currentLocationText || ''
      const newLaterality = this.selectedLaterality.toLowerCase()

      // match left or right followed by space, punctuation, hyphen, parenthesis or end
      const regex = /(left|right)(?=[\s\-\)\.,;:]|$)/i

      if (regex.test(text)) {
        // replace first occurrence only
        return text.replace(regex, newLaterality)
      }

      // no laterality found — prepend
      return `${newLaterality} ${text}`.trim()
    },
  },
  methods: {
    onCaptchaInput() {
      // Show error if input is not empty and does not match
      this.captchaError =
        this.captchaInput &&
        this.generatedCaptcha &&
        this.captchaInput.toUpperCase().trim() !== this.generatedCaptcha.toUpperCase().trim();
    },
    onCaptchaGenerated(text) {
      this.generatedCaptcha = text || ''
    },
    close() {
      this.internalModal = false
    },
    resetCaptcha() {
      this.captchaInput = ''
      this.captchaError = false
      if (this.$refs.captchaRef && typeof this.$refs.captchaRef.regenerate === 'function') {
        this.$refs.captchaRef.regenerate()
      }
    },
    save() {
      if (!this.captchaValid) return
      this.$emit('save', this.previewText)
      this.internalModal = false
      // Reset captcha after successful save so it won't retain previous value
      this.$nextTick(() => this.resetCaptcha())
    },
  },
}
</script>

<style scoped>
.modal-card {
  border-radius: 8px;
  padding: 8px 0;
}
.modal-title {
  font-size: 1.15em;
  font-weight: 600;
  color: #444;
  padding-bottom: 8px;
}
.modal-section {
  margin-bottom: 16px;
}
.modal-label {
  font-weight: 600;
  font-size: 0.98em;
  color: #444;
  margin-bottom: 2px;
}
.modal-current {
  color: #888;
  font-size: 1em;
  margin-top: 2px;
}
.modal-radio-group {
  margin-top: 4px;
  padding: 0;
}
.modal-preview-box {
  background: #e8f4fa;
  border-radius: 6px;
  padding: 12px 16px 12px 12px;
  margin-bottom: 18px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px;
}
.preview-arrow {
  font-size: 1.2em;
  font-weight: bold;
  margin-right: 2px;
}
.preview-label {
  font-weight: 600;
  font-size: 1em;
}
.preview-text {
  width: 100%;
  font-size: 1em;
  margin-left: 22px;
  margin-top: 2px;
}
.modal-captcha-section {
  margin-bottom: 8px;
}
.captcha-image-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.captcha-input {
  max-width: 200px;
  margin-top: 4px;
}
.modal-actions {
  display: flex;
  justify-content: space-between;
  padding: 12px 20px 16px 20px;
}
</style>
